/**
 * EnrollmentProgressTable
 *
 * Dense table for Application Progress tab: current stage, time in stage, next action, blockers.
 * UnifyTable; pagination sliced by parent. EnrollmentStatusBadge. PHI-safe.
 */

import { Text } from '@mantine/core';
import { useMemo } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { Enrollment } from '../types';
import { EnrollmentStatusBadge } from './EnrollmentStatusBadge';

interface EnrollmentProgressTableProps {
  enrollments: Enrollment[];
  page: number;
  pageSize: number;
}

function slicePage<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

function formatDaysInStage(days?: number): string {
  if (days === undefined) return '—';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `${days}d`;
}

const COLUMNS: UnifyTableColumn<Enrollment>[] = [
  {
    key: 'provider',
    label: 'Provider',
    render: (e) => (
      <Text size="sm" fw={500}>
        {e.providerName}
      </Text>
    ),
    getValue: (e) => e.providerName,
  },
  {
    key: 'payer',
    label: 'Payer',
    render: (e) => <Text size="sm">{e.payerName}</Text>,
    getValue: (e) => e.payerName,
  },
  {
    key: 'status',
    label: 'Status',
    render: (e) => <EnrollmentStatusBadge status={e.status} />,
    getValue: (e) => e.status,
  },
  {
    key: 'stage',
    label: 'Current stage',
    render: (e) => <Text size="sm">{e.currentStage ?? '—'}</Text>,
    getValue: (e) => e.currentStage ?? '',
  },
  {
    key: 'timeInStage',
    label: 'Time in stage',
    render: (e) => (
      <Text size="sm" c={e.daysInStage !== undefined && e.daysInStage < 0 ? 'red' : undefined}>
        {formatDaysInStage(e.daysInStage)}
      </Text>
    ),
    getValue: (e) => formatDaysInStage(e.daysInStage),
  },
  {
    key: 'nextAction',
    label: 'Next action',
    render: (e) => (
      <Text size="sm" lineClamp={2}>
        {e.nextAction ?? '—'}
      </Text>
    ),
    getValue: (e) => e.nextAction ?? '',
  },
  {
    key: 'blockers',
    label: 'Blockers',
    render: (e) => (
      <Text size="sm" c={e.blockers?.length ? 'orange' : 'dimmed'}>
        {e.blockers?.length ? e.blockers.join('; ') : '—'}
      </Text>
    ),
    getValue: (e) => e.blockers?.join('; ') ?? '',
  },
];

export function EnrollmentProgressTable({
  enrollments,
  page,
  pageSize,
}: EnrollmentProgressTableProps): JSX.Element {
  const rows = useMemo(() => slicePage(enrollments, page, pageSize), [enrollments, page, pageSize]);

  return (
    <UnifyTable withPaper={false} columns={COLUMNS} data={rows} getRowKey={(e) => e.id} />
  );
}
